import './index.css'

import Storage from 'disco-storage'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import type { Location, NavigateFunction } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import api from '@disco/api/server'
import { userTokenKey } from '@disco/constants/environment'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { getUser } from '@disco/state-management/selectors/user'
import { pushUINotification } from '@disco/utils/notifications'

import type { User } from 'types/user'

import DocumentHeadTags from 'components/document-head-tags'
import { Box } from 'components/ds'
import Button from 'components/ui/button'
import DiscoModal from 'components/ui/disco-modal'
import InlineLink from 'components/ui/inline-link'
import Input from 'components/ui/input'
import Label from 'components/ui/label'

type OwnState = {
  image: string | null | undefined
  code: string | null | undefined
  timeCode: string
  verifyInProgress: boolean
  methodId: number | null | undefined
  error: string | null | undefined
}

class MfaSetup extends React.Component<ComponentProps, OwnState> {
  state = {
    image: null,
    code: null,
    timeCode: '',
    verifyInProgress: false,
    methodId: null,
    error: null,
  }
  _ignoreClose = () => {}
  _onClose = () => {
    const returnPath = this.props?.location.state?.returnTo || '/'
    this.props.navigate(returnPath)
  }
  _onCodeChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({
      timeCode: e.target.value,
    })
  componentDidMount = () => {
    const { location } = this.props
    const { verified, key, qr_code, id } = location.state?.totpMethod || {}

    if (!verified && location.state) {
      this.setState({
        code: key,
        image: qr_code,
        methodId: id,
      })
    } else {
      this._onClose()
    }
  }
  _onVerify = async () => {
    try {
      this.setState({
        verifyInProgress: true,
      })
      const result = this.state.methodId
        ? await api.validateMfaCode(this.state.methodId, this.state.timeCode)
        : null

      if (result && result.verified) {
        pushUINotification({
          message: `Multi-factor authentication activated!`,
          level: 'success',
        })
        const userToken = await Storage.get(userTokenKey)
        if (userToken)
          Storage.set(userTokenKey, { ...userToken, loggedInWithMFA: true })

        this._onClose()
      } else if (result && !result.verified) {
        this.setState({
          error: 'Code is invalid!',
        })
      }
    } catch (error) {
      this.props.sendGenericError(error)
    } finally {
      this.setState({
        verifyInProgress: false,
      })
    }
  }

  render() {
    const header = 'Set up multi-factor authentication'
    return (
      <DiscoModal contentLabel={header} onRequestClose={this._ignoreClose}>
        <DocumentHeadTags title={`${header} | DISCO`} />
        <div className="Form">
          <h1 className="Form_Header">{header}</h1>
          {this.state.image && this.state.code && (
            <React.Fragment>
              <div className="mfa-container">
                <img className="mfa-qr-code" src={this.state.image} />
              </div>
              <div>
                <b>Step 1:</b> Open an authenticator app on your phone (such as
                Google or Microsoft Authenticator) and scan the QR code above, or
                manually enter the secret key below. The code you’ll need comes
                from this app, not from email.
              </div>
              <div className="mfa-secret">
                <span className="mfa-secret__title">Secret: </span>
                <span className="mfa-secret__code">{this.state.code}</span>
              </div>
              <div className="mfa-setup__step">
                <b>Step 2:</b> Once you’ve scanned the QR code or entered the
                secret key, your authenticator app will display a 6-digit code.
                Enter that code below to complete setup.
              </div>
              <Box mb="4">
                <Label caption="Code" validationError={this.state.error} />
                <Input
                  value={this.state.timeCode}
                  onChange={this._onCodeChange}
                  invalid={!!this.state.error}
                  autoFocus
                />
              </Box>
              {this.state.error && (
                <div className="mfa-setup__step">
                  If you are having trouble, please see our{' '}
                  <InlineLink
                    to="https://support.disco.ac/home/setting-up-and-using-multi-factor-authentication-mfa"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    helpful guide
                  </InlineLink>{' '}
                  on setting up and using multi-factor authentication.
                </div>
              )}
              <Box mb="4">
                <Button
                  caption="Verify"
                  progressCaption="Verifying"
                  progressIndicator={this.state.verifyInProgress}
                  onClick={this._onVerify}
                />
              </Box>
            </React.Fragment>
          )}
        </div>
      </DiscoModal>
    )
  }
}

const mapStateToProps = state => ({
  user: getUser(state),
})

const mapDispatchToProps = {
  sendGenericError,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

// HOC to provide navigate function to class component
const ConnectedMfaSetup = connector(MfaSetup)

const MfaSetupWithNavigate = props => {
  const navigate = useNavigate()
  return <ConnectedMfaSetup {...props} navigate={navigate} />
}

type PropsFromRedux = ConnectedProps<typeof connector>

interface AdditionalProps {
  navigate: NavigateFunction
  location: Location & {
    state: {
      totpMethod?: {
        verified?: boolean
        key?: string | null | undefined
        qr_code?: string | null | undefined
        id?: number | null | undefined
      }
      returnTo?: string
    }
  }
}

type ComponentProps = PropsFromRedux & AdditionalProps

export default MfaSetupWithNavigate
